import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Plus, Clock, Check, X, Sun, Moon, Calendar, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useCompanyScope } from '../context/CompanyScopeContext';

export default function OvertimeManagement() {
  const { user } = useAuth();
  const { selectedCompanyId, isCrossCompany } = useCompanyScope();
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    employee_id: '', date: new Date().toISOString().split('T')[0],
    hours: '', overtime_type: 'day', reason: ''
  });

  useEffect(() => { fetchRecords(); fetchEmployees(); }, []);

  const fetchRecords = async () => {
    try { setRecords((await api.get('/overtime-records')).data); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchEmployees = async () => {
    try { setEmployees((await api.get('/employees')).data); }
    catch (e) { console.error(e); }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/overtime-records', { ...form, hours: parseFloat(form.hours) });
      toast.success('Heures supplementaires enregistrees');
      setCreateOpen(false);
      setForm({ employee_id: '', date: new Date().toISOString().split('T')[0], hours: '', overtime_type: 'day', reason: '' });
      fetchRecords();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erreur'); }
  };

  const handleApprove = async (id) => {
    try { await api.post(`/overtime-records/${id}/approve`); toast.success('Valide'); fetchRecords(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Non autorise'); }
  };

  const handleReject = async (id) => {
    try { await api.post(`/overtime-records/${id}/reject`); toast.success('Refuse'); fetchRecords(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Non autorise'); }
  };

  const getStatusBadge = (status) => {
    const map = {
      pending_dept_head: { label: 'Chef direction', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
      pending_hr: { label: 'En attente RH', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
      pending_director: { label: 'En attente Directeur', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
      approved: { label: 'Approuve', cls: 'bg-green-100 text-green-700 border-green-200' },
      rejected: { label: 'Refuse', cls: 'bg-red-100 text-red-700 border-red-200' }
    };
    const v = map[status] || { label: status, cls: '' };
    return <Badge variant="outline" className={v.cls}>{v.label}</Badge>;
  };

  const getTypeInfo = (type) => {
    const map = {
      day: { label: 'Jour (+30%)', icon: <Sun className="w-4 h-4 text-amber-500" />, cls: 'text-amber-600' },
      night: { label: 'Nuit (+60%)', icon: <Moon className="w-4 h-4 text-indigo-500" />, cls: 'text-indigo-600' },
      holiday: { label: 'Ferie/Dim (+100%)', icon: <Calendar className="w-4 h-4 text-red-500" />, cls: 'text-red-600' }
    };
    return map[type] || map.day;
  };

  const canApprove = (record) => {
    const role = user?.role;
    const s = record.status;
    if (s === 'pending_dept_head') return role === 'company_admin' || role === 'super_admin';
    if (s === 'pending_hr') return role === 'hr' || role === 'company_admin' || role === 'super_admin';
    if (s === 'pending_director') return role === 'director' || role === 'company_admin' || role === 'super_admin';
    return false;
  };

  // Filtre par entreprise sélectionnée
  const scopedRecords = useMemo(() => {
    if (!isCrossCompany || !selectedCompanyId) return records;
    return records.filter(r => r.company_id === selectedCompanyId);
  }, [records, selectedCompanyId, isCrossCompany]);

  const scopedEmployees = useMemo(() => {
    if (!isCrossCompany || !selectedCompanyId) return employees;
    return employees.filter(e => e.company_id === selectedCompanyId);
  }, [employees, selectedCompanyId, isCrossCompany]);

  const pendingRecords = scopedRecords.filter(r => r.status.startsWith('pending'));
  const approvedRecords = scopedRecords.filter(r => r.status === 'approved');
  const rejectedRecords = scopedRecords.filter(r => r.status === 'rejected');

  if (loading) return <div className="text-center py-8">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="overtime-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Heures Supplementaires</h1>
          <p className="text-slate-600 mt-1">Validation en 3 niveaux : Chef direction → RH → Directeur</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="add-overtime-btn">
          <Plus className="w-4 h-4 mr-2" /> Declarer des HS
        </Button>
      </div>

      {/* Approval flow legend */}
      <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 rounded-lg p-3 border">
        <span className="font-medium">Flux :</span>
        <Badge variant="outline" className="bg-amber-50 text-amber-600">Chef direction</Badge>
        <ArrowRight className="w-3 h-3" />
        <Badge variant="outline" className="bg-blue-50 text-blue-600">RH</Badge>
        <ArrowRight className="w-3 h-3" />
        <Badge variant="outline" className="bg-purple-50 text-purple-600">Directeur</Badge>
        <ArrowRight className="w-3 h-3" />
        <Badge variant="outline" className="bg-green-50 text-green-600">Approuve</Badge>
      </div>

      {/* Pending */}
      {pendingRecords.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader>
            <CardTitle className="text-amber-700 flex items-center gap-2">
              <Clock className="w-5 h-5" /> En attente ({pendingRecords.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {pendingRecords.map(r => {
              const typeInfo = getTypeInfo(r.overtime_type);
              return (
                <div key={r.id} className="flex items-center justify-between p-3 bg-white rounded-lg border" data-testid={`ot-${r.id}`}>
                  <div className="flex items-center gap-3">
                    {typeInfo.icon}
                    <div>
                      <p className="font-medium">{r.employee_name}</p>
                      <p className="text-sm text-slate-500">
                        {r.date} - <span className={typeInfo.cls}>{r.hours}h {typeInfo.label}</span>
                      </p>
                      {r.reason && <p className="text-xs text-slate-400">{r.reason}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(r.status)}
                    {canApprove(r) && (
                      <>
                        <Button size="sm" onClick={() => handleApprove(r.id)} data-testid={`approve-ot-${r.id}`}>
                          <Check className="w-3 h-3 mr-1" /> Valider
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleReject(r.id)} data-testid={`reject-ot-${r.id}`}>
                          <X className="w-3 h-3" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Approved */}
      <Card className="border-slate-200">
        <CardHeader><CardTitle className="text-green-700">Approuvees ({approvedRecords.length})</CardTitle></CardHeader>
        <CardContent>
          {approvedRecords.length === 0 ? (
            <p className="text-slate-500 text-center py-4">Aucune HS approuvee</p>
          ) : (
            <div className="space-y-2">
              {approvedRecords.map(r => {
                const t = getTypeInfo(r.overtime_type);
                return (
                  <div key={r.id} className="flex items-center justify-between p-2 bg-green-50 rounded border border-green-200" data-testid={`ot-${r.id}`}>
                    <div className="flex items-center gap-2">{t.icon}<span className="font-medium text-sm">{r.employee_name}</span><span className="text-sm text-slate-500">{r.date}</span></div>
                    <div className="flex items-center gap-2"><span className={`font-bold text-sm ${t.cls}`}>{r.hours}h x{r.rate_multiplier}</span>{getStatusBadge(r.status)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rejected */}
      {rejectedRecords.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-red-600">Refusees ({rejectedRecords.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {rejectedRecords.slice(0, 10).map(r => (
                <div key={r.id} className="flex items-center justify-between p-2 opacity-60">
                  <span className="text-sm">{r.employee_name} - {r.date} - {r.hours}h</span>
                  {getStatusBadge(r.status)}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Declarer des heures supplementaires</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label>Employe *</Label>
              <select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} className="w-full rounded-md border border-slate-300 p-2" required data-testid="ot-employee-select">
                <option value="">-- Choisir --</option>
                {scopedEmployees.filter(e => e.status === 'active').map(emp => (
                  <option key={emp.id} value={emp.id}>{emp.first_name} {emp.last_name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required data-testid="ot-date-input" />
              </div>
              <div className="space-y-2">
                <Label>Heures *</Label>
                <Input type="number" step="0.5" min="0.5" value={form.hours} onChange={(e) => setForm({ ...form, hours: e.target.value })} required data-testid="ot-hours-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Type *</Label>
              <select value={form.overtime_type} onChange={(e) => setForm({ ...form, overtime_type: e.target.value })} className="w-full rounded-md border border-slate-300 p-2" data-testid="ot-type-select">
                <option value="day">Jour (+30%)</option>
                <option value="night">Nuit (+60%)</option>
                <option value="holiday">Dimanche / Jour ferie (+100%)</option>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Motif</Label>
              <Textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} rows={2} data-testid="ot-reason-input" />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)} className="flex-1">Annuler</Button>
              <Button type="submit" className="flex-1" data-testid="ot-submit-btn">Enregistrer</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
